import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import {
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where
} from "../utility/firestoreGateway";
import { getAuth } from "firebase/auth";
import {
  readIndexedDbValue,
  writeIndexedDbValue
} from "../utility/indexedDbCache";
import { WEEKLY_QUEST_PERSON_METADATA_VERSION } from "../utility/weeklyQuestCardRefresh";
import {
  buildCompactRatingsCacheData,
  hydrateRatingsCacheData,
  reconcileRatingsWithHistoryTombstones,
  reviveRatingsCacheDate,
  USER_RATINGS_CACHE_VERSION
} from "../utility/userRatingsCache";
import type { RatingsCacheData } from "../utility/userRatingsCache";
import {
  normalizeRatingsSyncMetadata,
  RATINGS_SYNC_VERSION
} from "../utility/ratingsSync";
import {
  compactFilmCredits,
  isDirectorCrewMember
} from "../utility/compactFilmCredits";
import { isDemoUserId } from "../utility/demoSession";
import type { Rating, RatingHistoryEntry } from "../types/rating";
import type { FilmMetadata, PersonCredit, EntityId } from "../types/media";
import type { QueryDocumentSnapshot, QuerySnapshot } from "firebase/firestore";
import { isUnknownArray, isTimestamp, isRecord, errorMessage } from "../types/data";

interface RatingsCachePayload {
  ratings: Rating[];
  ratingHistory: RatingHistoryEntry[];
  filmMap: Record<string, FilmMetadata>;
  lastServerTimestamp: number;
  lastFullFetchAt: number;
  lastSyncedAt: number;
  serverCount: number;
  lastCountCheckedAt: number;
  historyLastServerTimestamp: number;
  historyLastFullFetchAt: number;
  historyLastSyncedAt: number;
  historyServerCount: number;
  serverRevision: number;
  posterRepairVersion: number;
}
interface RatingsSyncState {
  userId: string | null;
  loaded: boolean;
  exists: boolean;
  revision: number;
  ratingsCount: number | null;
  updatedAt: number;
}

export interface UserRatingsContextValue {
  userId: string | null;
  ratings: Rating[];
  ratingHistory: RatingHistoryEntry[];
  loading: boolean;
  historyLoading: boolean;
  initialSyncLoading: boolean;
  ratingsSyncRevision: number;
  ratingsSyncReady: boolean;
  error: Error | null;
  upsertRating: (rating: Rating) => void;
  upsertRatings: (ratings: Rating[]) => void;
  upsertRatingHistory: (history: RatingHistoryEntry) => void;
  removeRating: (filmId: EntityId) => void;
}

type SnapshotLoadResult =
  | { status: "fulfilled"; value: QuerySnapshot<Rating> }
  | { status: "rejected"; reason: unknown };

export const UserRatingsContext = createContext<UserRatingsContextValue>({
  userId: null,
  ratings: [],
  ratingHistory: [],
  loading: true,
  historyLoading: true,
  initialSyncLoading: true,
  ratingsSyncRevision: 0,
  ratingsSyncReady: false,
  error: null,
  upsertRating: () => {},
  upsertRatings: () => {},
  upsertRatingHistory: () => {},
  removeRating: () => {}
});

const FILM_CHUNK_SIZE = 10;
const FILM_QUERY_CONCURRENCY = 6;
const COUNT_SYNC_TTL = 6 * 60 * 60 * 1000;
const FULL_REFRESH_TTL = 7 * 24 * 60 * 60 * 1000;
const HISTORY_DELTA_SYNC_TTL = 6 * 60 * 60 * 1000;
const HISTORY_FULL_REFRESH_TTL = 7 * 24 * 60 * 60 * 1000;
// v6 keeps one canonical ratings copy; active history and film metadata are
// reconstructed in memory instead of being duplicated in IndexedDB.
const CACHE_VERSION = USER_RATINGS_CACHE_VERSION;
const POSTER_REPAIR_VERSION = 2;
const cacheWriteQueues = new Map<string, Promise<unknown>>();
const toOptionalCount = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : null;
};

const chunk = <T,>(items: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
};

const mapWithConcurrency = async <T, R>(items: T[], concurrency: number, mapper: (item: T, index: number) => R | Promise<R>): Promise<R[]> => {
  if (!items.length) return [];

  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  };

  await Promise.all(
    Array.from(
      { length: Math.min(Math.max(1, concurrency), items.length) },
      () => worker()
    )
  );
  return results;
};

const extractDirectors = (crew: PersonCredit[] = []) =>
  crew.filter(isDirectorCrewMember);

const getCreditsSignature = ({ cast = [], crew = [] }: { cast?: PersonCredit[]; crew?: PersonCredit[] } = {}) =>
  JSON.stringify(compactFilmCredits({ cast, crew }));

const getStoredMediaType = (film: FilmMetadata = {}) => {
  const mediaType = String(
    film.mediaType || film.media_type || film.type || ""
  ).toLowerCase();
  if (["tv", "series", "сериал"].includes(mediaType)) return "tv";
  if (["movie", "film", "фильм"].includes(mediaType)) return "movie";
  if (film.first_air_date || isUnknownArray(film.seasons)) return "tv";
  return "";
};

const resolveRuntime = (...values: unknown[]) => {
  for (const value of values) {
    if (isUnknownArray(value)) {
      const runtime = value.find((item) => Number(item) > 0);
      if (Number(runtime) > 0) return Number(runtime);
      continue;
    }

    const runtime = Number(value);
    if (Number.isFinite(runtime) && runtime > 0) return runtime;
  }

  return null;
};

const normalizeFilmMetadata = (filmId: EntityId | undefined, ratingData: Rating = {}, filmData: FilmMetadata = {}): Rating => {
  const crew = ratingData.crew || filmData.crew || [];
  const directors =
    ratingData.directors ||
    filmData.directors ||
    (crew.length > 0 ? extractDirectors(crew) : []);

  return {
    filmId,
    ...filmData,
    ...ratingData,
    title:
      ratingData.title ||
      filmData.title ||
      ratingData.filmTitle ||
      ratingData.name ||
      "Неизвестный фильм",
    originalTitle:
      ratingData.originalTitle ||
      filmData.originalTitle ||
      ratingData.filmOriginalTitle ||
      null,
    overview:
      ratingData.overview || filmData.overview || ratingData.filmOverview || "",
    rate:
      ratingData.rate ?? filmData.rate ?? ratingData.filmVoteAverage ?? 0,
    voteCount:
      ratingData.voteCount ?? filmData.voteCount ?? ratingData.filmVoteCount ?? 0,
    releaseDate:
      ratingData.releaseDate ||
      filmData.releaseDate ||
      ratingData.filmReleaseDate ||
      ratingData.first_air_date ||
      "",
    posterPath:
      ratingData.posterPath ||
      ratingData.poster_path ||
      ratingData.filmData?.posterPath ||
      ratingData.filmData?.poster_path ||
      ratingData.fullDataFilm?.poster_path ||
      filmData.posterPath ||
      filmData.poster_path ||
      ratingData.filmPoster ||
      "",
    runtime: resolveRuntime(
      ratingData.runtime,
      ratingData.filmRuntime,
      ratingData.filmData?.runtime,
      ratingData.fullDataFilm?.runtime,
      filmData.runtime,
      filmData.filmRuntime,
      ratingData.episode_run_time,
      ratingData.filmData?.episode_run_time,
      ratingData.fullDataFilm?.episode_run_time,
      filmData.episode_run_time
    ),
    episode_run_time:
      ratingData.episode_run_time ||
      ratingData.filmData?.episode_run_time ||
      ratingData.fullDataFilm?.episode_run_time ||
      filmData.episode_run_time ||
      null,
    genres: ratingData.genres || filmData.genres || ratingData.filmGenres || [],
    originCountry:
      ratingData.originCountry ||
      filmData.originCountry ||
      ratingData.productionCountry ||
      [],
    productionCountries:
      ratingData.productionCountries || filmData.productionCountries || [],
    directors,
    seasons: ratingData.seasons || filmData.seasons || null
  };
};

const toRatingEntry = (docSnapshot: QueryDocumentSnapshot<Rating>): Rating => {
  const data = docSnapshot.data();
  return {
    filmId: docSnapshot.id,
    ...data,
    ratedAt: isTimestamp(data.ratedAt) ? data.ratedAt.toDate() : data.ratedAt || null,
    firstRatedAt: isTimestamp(data.firstRatedAt) ? data.firstRatedAt.toDate() : data.firstRatedAt || null,
    firstTrackedAt: isTimestamp(data.firstTrackedAt) ? data.firstTrackedAt.toDate() : data.firstTrackedAt || null,
    overallRatedAt: isTimestamp(data.overallRatedAt) ? data.overallRatedAt.toDate() : data.overallRatedAt || null,
    detailedRatedAt: isTimestamp(data.detailedRatedAt) ? data.detailedRatedAt.toDate() : data.detailedRatedAt || null,
    seasonRatingsUpdatedAt: isTimestamp(data.seasonRatingsUpdatedAt) ? data.seasonRatingsUpdatedAt.toDate() : data.seasonRatingsUpdatedAt || null,
    updatedAt: isTimestamp(data.updatedAt) ? data.updatedAt.toDate() : data.updatedAt || null
  };
};

const toHistoryEntry = (docSnapshot: QueryDocumentSnapshot<RatingHistoryEntry>): RatingHistoryEntry => {
  const data = docSnapshot.data();
  return {
    filmId: docSnapshot.id,
    ...data,
    ratedAt: isTimestamp(data.ratedAt) ? data.ratedAt.toDate() : data.ratedAt || null,
    firstRatedAt: isTimestamp(data.firstRatedAt) ? data.firstRatedAt.toDate() : data.firstRatedAt || null,
    firstTrackedAt: isTimestamp(data.firstTrackedAt) ? data.firstTrackedAt.toDate() : data.firstTrackedAt || null,
    overallRatedAt: isTimestamp(data.overallRatedAt) ? data.overallRatedAt.toDate() : data.overallRatedAt || null,
    detailedRatedAt: isTimestamp(data.detailedRatedAt) ? data.detailedRatedAt.toDate() : data.detailedRatedAt || null,
    seasonRatingsUpdatedAt: isTimestamp(data.seasonRatingsUpdatedAt) ? data.seasonRatingsUpdatedAt.toDate() : data.seasonRatingsUpdatedAt || null,
    lastRatedAt: isTimestamp(data.lastRatedAt) ? data.lastRatedAt.toDate() : data.lastRatedAt || null,
    deletedAt: isTimestamp(data.deletedAt) ? data.deletedAt.toDate() : data.deletedAt || null,
    updatedAt: isTimestamp(data.updatedAt) ? data.updatedAt.toDate() : data.updatedAt || null
  };
};

const historyTombstoneMatchesActiveLifecycle = (activeEntry: Rating = {}, historyEntry: RatingHistoryEntry = {}) => {
  const activeLifecycleId = String(activeEntry.ratingLifecycleId || "");
  const historyLifecycleId = String(historyEntry.ratingLifecycleId || "");
  return (
    !activeLifecycleId ||
    !historyLifecycleId ||
    activeLifecycleId === historyLifecycleId
  );
};

const mergeRatingsWithDeletedHistory = (ratings: Rating[] = [], deletedHistory: RatingHistoryEntry[] = []) => {
  const byFilmId = new Map<string, RatingHistoryEntry>();
  ratings.forEach((rating) => {
    const filmId = String(rating?.filmId || rating?.id || "");
    if (!filmId) return;
    byFilmId.set(filmId, {
      ...rating,
      filmId,
      active: rating.active !== false,
      lastRatedAt: rating.lastRatedAt || rating.ratedAt || null,
      deletedAt: null
    });
  });
  deletedHistory.forEach((entry) => {
    const filmId = String(entry?.filmId || entry?.id || "");
    if (!filmId) return;
    const activeEntry = byFilmId.get(filmId);
    if (activeEntry) {
      if (!historyTombstoneMatchesActiveLifecycle(activeEntry, entry)) return;
      const activeUpdatedAt = getEntryUpdatedAt(activeEntry);
      const deletedUpdatedAt = getDeletionUpdatedAt(entry);
      // A tombstone wins only over a stale active value. If the same film was
      // added again after deletion, the newer active lifecycle must stay
      // visible to expeditions and weekly cards.
      if (!deletedUpdatedAt || activeUpdatedAt >= deletedUpdatedAt) return;
    }
    byFilmId.set(filmId, { ...(activeEntry || {}), ...entry, filmId });
  });
  return [...byFilmId.values()];
};

const hasUsefulFilmMetadata = (rating: FilmMetadata = {}, film: FilmMetadata = {}) => {
  const ratingDetails = rating.filmData || rating.fullDataFilm || {};
  const filmDetails = film.filmData || film.fullDataFilm || {};
  const title =
    rating.title ||
    rating.filmTitle ||
    rating.name ||
    ratingDetails.title ||
    ratingDetails.name ||
    film.title ||
    film.filmTitle ||
    film.name ||
    filmDetails.title ||
    filmDetails.name;
  const releaseDate =
    rating.releaseDate ||
    rating.filmReleaseDate ||
    rating.first_air_date ||
    ratingDetails.releaseDate ||
    ratingDetails.release_date ||
    ratingDetails.first_air_date ||
    film.releaseDate ||
    film.filmReleaseDate ||
    film.first_air_date ||
    filmDetails.releaseDate ||
    filmDetails.release_date ||
    filmDetails.first_air_date;
  const poster =
    rating.posterPath ||
    rating.poster_path ||
    rating.filmPoster ||
    ratingDetails.posterPath ||
    ratingDetails.poster_path ||
    film.posterPath ||
    film.poster_path ||
    film.filmPoster ||
    filmDetails.posterPath ||
    filmDetails.poster_path;
  const hasRuntime = [
    rating.runtime,
    rating.filmRuntime,
    rating.episode_run_time,
    ratingDetails.runtime,
    ratingDetails.episode_run_time,
    film.runtime,
    film.filmRuntime,
    film.episode_run_time,
    filmDetails.runtime,
    filmDetails.episode_run_time
  ].some((runtime) =>
    isUnknownArray(runtime)
      ? runtime.some((value) => Number(value) > 0)
      : Number(runtime) > 0
  );
  const hasGenres = [
    rating.genres,
    rating.filmGenres,
    ratingDetails.genres,
    film.genres,
    film.filmGenres,
    filmDetails.genres
  ].some((genres) => isUnknownArray(genres) && genres.length > 0);
  const hasCountries = [
    rating.originCountry,
    rating.productionCountries,
    rating.productionCountry,
    ratingDetails.originCountry,
    ratingDetails.origin_country,
    ratingDetails.productionCountries,
    ratingDetails.production_countries,
    film.originCountry,
    film.productionCountries,
    film.productionCountry,
    filmDetails.originCountry,
    filmDetails.origin_country,
    filmDetails.productionCountries,
    filmDetails.production_countries
  ].some((countries) =>
    isUnknownArray(countries) ? countries.length > 0 : Boolean(countries)
  );
  const hasSeasons = [
    rating.seasons,
    ratingDetails.seasons,
    film.seasons,
    filmDetails.seasons
  ].some((seasons) => isUnknownArray(seasons) && seasons.length > 0);

  return Boolean(
    title &&
      (releaseDate ||
      poster ||
      hasRuntime ||
      hasGenres ||
      hasCountries ||
      hasSeasons)
  );
};

const mergeRatingsWithFilms = (ratingEntries: Rating[], filmMap: Record<string, FilmMetadata>) =>
  ratingEntries.map((rating) =>
    normalizeFilmMetadata(rating.filmId, rating, filmMap[String(rating.filmId)] || {})
  );

const reviveDate = reviveRatingsCacheDate;

const getLatestServerTimestamp = (ratings: Rating[] = []) =>
  ratings.reduce((latest, rating) => {
    const value = reviveDate(rating?.updatedAt || rating?.ratedAt)?.getTime();
    return typeof value === "number" && Number.isFinite(value) ? Math.max(latest, value) : latest;
  }, 0);

const getEntryUpdatedAt = (entry: Rating = {}) =>
  reviveDate(entry.updatedAt || entry.lastRatedAt || entry.ratedAt)?.getTime() || 0;

const getDeletionUpdatedAt = (entry: RatingHistoryEntry = {}) =>
  reviveDate(entry.updatedAt || entry.deletedAt || entry.lastRatedAt || entry.ratedAt)?.getTime() || 0;

const preferNewestEntry = (currentEntry: Rating, incomingEntry: Rating | undefined): Rating => {
  if (!currentEntry && incomingEntry) return incomingEntry;
  if (!incomingEntry) return currentEntry;
  return getEntryUpdatedAt(currentEntry) > getEntryUpdatedAt(incomingEntry)
    ? currentEntry
    : incomingEntry;
};

const saveToCache = (userId: string | null | undefined, data: Partial<RatingsCachePayload>) => {
  const cacheKey = `userRatings_${userId}`;
  const compactData = buildCompactRatingsCacheData(data);
  const previousWrite = cacheWriteQueues.get(cacheKey) || Promise.resolve();
  const nextWrite = previousWrite
    .catch(() => undefined)
    .then(() => writeIndexedDbValue(cacheKey, {
      ...compactData,
      version: CACHE_VERSION,
      savedAt: Date.now()
    }));
  cacheWriteQueues.set(cacheKey, nextWrite);
  const clearPendingWrite = () => {
    if (cacheWriteQueues.get(cacheKey) === nextWrite) {
      cacheWriteQueues.delete(cacheKey);
    }
  };
  void nextWrite.then(clearPendingWrite, clearPendingWrite);
  return nextWrite;
};

const migrateCachedData = hydrateRatingsCacheData;

export const loadUserRatingsFromCache = async (userId: string) => {
  const cacheKey = `userRatings_${userId}`;
  let cached: RatingsCacheData | null | undefined = null;
  try {
    const pendingWrite = cacheWriteQueues.get(cacheKey);
    if (pendingWrite) {
      await pendingWrite.catch(() => undefined);
    }
    cached = await readIndexedDbValue<RatingsCacheData>(cacheKey);
  } catch (err) {
    console.warn("Failed to read ratings from IndexedDB:", err);
  }

  if (cached && isUnknownArray(cached.ratings)) {
    try {
      const migrated = migrateCachedData(cached);
      if (cached.version !== CACHE_VERSION) {
        try {
          await saveToCache(userId, migrated);
        } catch (err) {
          // A quota/write failure must not discard a cache that was read and
          // decoded successfully; doing so would trigger a full Firestore scan.
          console.warn("Failed to rewrite compact ratings cache:", err);
        }
      }
      return migrated;
    } catch (err) {
      // A malformed or partially written IndexedDB value must fall back to
      // Firestore. Letting this rejection escape leaves every ratings-backed
      // page on its initial loading state.
      console.warn("Failed to decode ratings cache:", err);
      return null;
    }
  }

  // Однократная миграция старого localStorage-кэша. Его размер был главной
  // причиной повторных полных чтений при превышении quota.
  try {
    const legacyRaw = localStorage.getItem(cacheKey);
    if (!legacyRaw) return null;
    const legacy: unknown = JSON.parse(legacyRaw);
    if (!isRecord(legacy) || !isUnknownArray(legacy.ratings)) return null;
    const migrated = migrateCachedData(legacy);
    try {
      await saveToCache(userId, migrated);
      localStorage.removeItem(cacheKey);
    } catch (err) {
      // Keep the legacy value for the next session, but use it now. Falling
      // through to Firestore would turn a local quota issue into thousands of
      // billed reads on every mount.
      console.warn("Failed to migrate legacy ratings cache to IndexedDB:", err);
    }
    return migrated;
  } catch (err) {
    console.warn("Failed to load ratings from cache:", err);
    return null;
  }
};

export const useUserRatings = () => {
  return useContext(UserRatingsContext);
};

export function UserRatingsProvider({ children, userId, enabled = true }: { children?: React.ReactNode; userId?: string | null; enabled?: boolean }) {
  const isDemoSession = isDemoUserId(userId);
  const [ratingsOwnerUserId, setRatingsOwnerUserId] = useState(userId || null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [ratingHistory, setRatingHistory] = useState<RatingHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [initialSyncLoading, setInitialSyncLoading] = useState(true);
  const [ratingsSyncMetadata, setRatingsSyncMetadata] = useState<RatingsSyncState>({
    userId: null,
    loaded: false,
    exists: false,
    revision: 0,
    ratingsCount: null,
    updatedAt: 0
  });
  const [error, setError] = useState<Error | null>(null);
  const filmMapRef = useRef<Record<string, FilmMetadata>>({});
  const ratingsStateRef = useRef<Rating[]>([]);
  const ratingHistoryStateRef = useRef<RatingHistoryEntry[]>([]);
  const serverTimestampRef = useRef(0);
  const lastFullFetchAtRef = useRef(0);
  const lastSyncedAtRef = useRef(0);
  const serverCountRef = useRef(0);
  const lastCountCheckedAtRef = useRef(0);
  const historyTimestampRef = useRef(0);
  const historyLastFullFetchAtRef = useRef(0);
  const historyLastSyncedAtRef = useRef(0);
  const historyServerCountRef = useRef(0);
  const serverRevisionRef = useRef(0);
  const hydrationCompleteRef = useRef(false);
  const earlyCacheHydrationRef = useRef(0);

  // Layout normally remounts the provider when its profile scope changes.
  // Keep the provider safe on its own as well: during a same-instance switch,
  // children first see the old owner id (so mutations are rejected), then a
  // fully empty state for the new owner. Old profile entries are never exposed
  // under the new uid while its cache is hydrating.
  useLayoutEffect(() => {
    const nextOwnerUserId = userId || null;
    if (ratingsOwnerUserId === nextOwnerUserId) return;

    setRatingsOwnerUserId(nextOwnerUserId);
    ratingsStateRef.current = [];
    ratingHistoryStateRef.current = [];
    filmMapRef.current = {};
    serverTimestampRef.current = 0;
    lastFullFetchAtRef.current = 0;
    lastSyncedAtRef.current = 0;
    serverCountRef.current = 0;
    lastCountCheckedAtRef.current = 0;
    historyTimestampRef.current = 0;
    historyLastFullFetchAtRef.current = 0;
    historyLastSyncedAtRef.current = 0;
    historyServerCountRef.current = 0;
    serverRevisionRef.current = 0;
    hydrationCompleteRef.current = false;
    setRatings([]);
    setRatingHistory([]);
    setError(null);
    setLoading(Boolean(enabled && nextOwnerUserId));
    setHistoryLoading(Boolean(enabled && nextOwnerUserId));
    setInitialSyncLoading(Boolean(enabled && nextOwnerUserId));
  }, [enabled, ratingsOwnerUserId, userId]);

  // IndexedDB is the fastest complete snapshot we have.  Do not wait for the
  // user-document revision listener before showing it: on a throttled mobile
  // connection that small Firestore round trip used to keep every statistics
  // and game screen behind a spinner for many seconds.  The regular hydration
  // below still performs the authoritative server reconciliation and is the
  // only path that marks ratingsSyncReady, so cached data can paint early
  // without allowing progress/XP mutations against an unverified snapshot.
  useEffect(() => {
    if (!enabled || !userId || ratingsOwnerUserId !== userId) return undefined;

    const requestId = earlyCacheHydrationRef.current + 1;
    earlyCacheHydrationRef.current = requestId;
    let cancelled = false;

    void loadUserRatingsFromCache(userId)
      .then((cachedData) => {
        if (
          cancelled ||
          earlyCacheHydrationRef.current !== requestId ||
          hydrationCompleteRef.current ||
          !cachedData
        ) {
          return;
        }

        const cachedRatings = isUnknownArray(cachedData.ratings)
          ? cachedData.ratings
          : [];
        const cachedHistory = isUnknownArray(cachedData.ratingHistory)
          ? cachedData.ratingHistory
          : [];

        ratingsStateRef.current = cachedRatings;
        ratingHistoryStateRef.current = cachedHistory;
        filmMapRef.current = cachedData.filmMap || {};
        serverTimestampRef.current = Number(cachedData.lastServerTimestamp) || 0;
        lastFullFetchAtRef.current = Number(cachedData.lastFullFetchAt) || 0;
        lastSyncedAtRef.current = Number(cachedData.lastSyncedAt) || 0;
        serverCountRef.current = Number(cachedData.serverCount) || cachedRatings.length;
        lastCountCheckedAtRef.current = Number(cachedData.lastCountCheckedAt) || 0;
        historyTimestampRef.current = Number(cachedData.historyLastServerTimestamp) || 0;
        historyLastFullFetchAtRef.current = Number(cachedData.historyLastFullFetchAt) || 0;
        historyLastSyncedAtRef.current = Number(cachedData.historyLastSyncedAt) || 0;
        historyServerCountRef.current =
          Number(cachedData.historyServerCount) || cachedHistory.length;
        serverRevisionRef.current = Number(cachedData.serverRevision) || 0;

        setRatings(cachedRatings);
        setRatingHistory(cachedHistory);
        setLoading(false);
        setHistoryLoading(false);
        setError(null);
      })
      .catch((cacheError: unknown) => {
        // The authoritative hydration will fall back to Firestore.  A broken
        // local cache must not turn into an unhandled rejection.
        console.warn("Failed to hydrate ratings cache early:", cacheError);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, ratingsOwnerUserId, userId]);

  const buildCachePayload = (overrides: Partial<RatingsCachePayload> = {}): RatingsCachePayload => ({
    ratings: ratingsStateRef.current,
    ratingHistory: ratingHistoryStateRef.current,
    filmMap: filmMapRef.current,
    lastServerTimestamp: serverTimestampRef.current,
    lastFullFetchAt: lastFullFetchAtRef.current,
    lastSyncedAt: lastSyncedAtRef.current,
    serverCount: serverCountRef.current,
    lastCountCheckedAt: lastCountCheckedAtRef.current,
    historyLastServerTimestamp: historyTimestampRef.current,
    historyLastFullFetchAt: historyLastFullFetchAtRef.current,
    historyLastSyncedAt: historyLastSyncedAtRef.current,
    historyServerCount: historyServerCountRef.current,
    serverRevision: serverRevisionRef.current,
    posterRepairVersion: POSTER_REPAIR_VERSION,
    ...overrides
  });

  const saveCacheSnapshot = (overrides: Partial<RatingsCachePayload> = {}) =>
    saveToCache(userId, buildCachePayload(overrides));

  const persistRatings = (nextRatings: Rating[], nextFilmMap: Record<string, FilmMetadata>, lastServerTimestamp?: number, syncMeta: Partial<RatingsCachePayload> = {}) => {
    if (lastServerTimestamp !== undefined) {
      serverTimestampRef.current = lastServerTimestamp;
    }
    if (syncMeta.lastFullFetchAt !== undefined) {
      lastFullFetchAtRef.current = Number(syncMeta.lastFullFetchAt) || 0;
    }
    if (syncMeta.lastSyncedAt !== undefined) {
      lastSyncedAtRef.current = Number(syncMeta.lastSyncedAt) || 0;
    }
    if (syncMeta.serverCount !== undefined) {
      serverCountRef.current = Number(syncMeta.serverCount) || 0;
    }
    if (syncMeta.lastCountCheckedAt !== undefined) {
      lastCountCheckedAtRef.current = Number(syncMeta.lastCountCheckedAt) || 0;
    }
    filmMapRef.current = nextFilmMap;
    ratingsStateRef.current = nextRatings;
    setRatings(nextRatings);
    setLoading(false);
    setError(null);

    void saveCacheSnapshot({
      ratings: nextRatings,
      filmMap: nextFilmMap
    }).catch((err: unknown) => {
      console.warn(
        "Failed to save ratings to IndexedDB:",
        err
      );
    });
  };

  const persistRatingHistory = (nextHistory: RatingHistoryEntry[], nextFilmMap: Record<string, FilmMetadata>, lastHistoryTimestamp?: number, syncMeta: Partial<RatingsCachePayload> = {}) => {
    if (lastHistoryTimestamp !== undefined) {
      historyTimestampRef.current = Number(lastHistoryTimestamp) || 0;
    }
    if (syncMeta.historyLastFullFetchAt !== undefined) {
      historyLastFullFetchAtRef.current = Number(syncMeta.historyLastFullFetchAt) || 0;
    }
    if (syncMeta.historyLastSyncedAt !== undefined) {
      historyLastSyncedAtRef.current = Number(syncMeta.historyLastSyncedAt) || 0;
    }
    if (syncMeta.historyServerCount !== undefined) {
      historyServerCountRef.current = Number(syncMeta.historyServerCount) || 0;
    }

    filmMapRef.current = nextFilmMap;
    ratingHistoryStateRef.current = nextHistory;
    setRatingHistory(nextHistory);
    setHistoryLoading(false);

    void saveCacheSnapshot({
      ratingHistory: nextHistory,
      filmMap: nextFilmMap
    }).catch((err: unknown) => {
      console.warn("Failed to save rating history to IndexedDB:", err);
    });
  };

  const canMutateRatings = () =>
    Boolean(
      enabled &&
      userId &&
      ratingsOwnerUserId === userId &&
      getAuth().currentUser?.uid === userId
    );

  const upsertRatings = (ratingEntries: Rating[] = []) => {
    if (
      !canMutateRatings() ||
      !isUnknownArray(ratingEntries) ||
      ratingEntries.length === 0
    ) return;

    const currentRatings = isUnknownArray(ratingsStateRef.current)
      ? ratingsStateRef.current
      : [];
    const nextFilmMap = { ...filmMapRef.current };
    const normalizedEntries = ratingEntries
      .filter((entry) => entry?.filmId)
      .map((entry) => {
        const filmId = String(entry.filmId);
        const normalized = normalizeFilmMetadata(
          filmId,
          { ...entry, filmId },
          nextFilmMap[filmId] || {}
        );
        nextFilmMap[filmId] = {
          ...(nextFilmMap[filmId] || {}),
          ...normalized
        };
        return normalized;
      });

    if (!normalizedEntries.length) return;

    const changedById = new Map(
      normalizedEntries.map((entry) => [String(entry.filmId), entry])
    );
    const updatedExistingRatings = currentRatings.map((item) => {
      const key = String(item.filmId || item.id);
      return changedById.has(key) ? { ...item, ...changedById.get(key) } : item;
    });
    const existingIds = new Set(
      currentRatings.map((item) => String(item.filmId || item.id))
    );
    const newRatings = normalizedEntries.filter(
      (entry) => !existingIds.has(String(entry.filmId))
    );
    const nextRatings = [...newRatings, ...updatedExistingRatings];

    persistRatings(nextRatings, nextFilmMap, undefined, {
      lastSyncedAt: Date.now()
    });
  };

  const upsertRating = (ratingEntry: Rating) => {
    if (!canMutateRatings() || !ratingEntry?.filmId) return;

    upsertRatings([ratingEntry]);
  };

  const upsertRatingHistory = (historyEntry: RatingHistoryEntry) => {
    if (!canMutateRatings() || !historyEntry?.filmId) return;

    const filmId = String(historyEntry.filmId);
    const currentHistory = isUnknownArray(ratingHistoryStateRef.current)
      ? ratingHistoryStateRef.current
      : [];
    const existingEntry = currentHistory.find(
      (item) => String(item.filmId || item.id) === filmId
    );
    const normalized = normalizeFilmMetadata(
      filmId,
      {
        ...(existingEntry || {}),
        ...historyEntry,
        filmId
      },
      filmMapRef.current[filmId] || {}
    );
    const remaining = currentHistory.filter(
      (item) => String(item.filmId || item.id) !== filmId
    );
    const nextHistory = [normalized, ...remaining];
    const now = Date.now();
    persistRatingHistory(
      nextHistory,
      {
        ...filmMapRef.current,
        [filmId]: {
          ...(filmMapRef.current[filmId] || {}),
          ...normalized
        }
      },
      historyTimestampRef.current,
      {
        historyLastSyncedAt: now,
        historyServerCount: Math.max(historyServerCountRef.current, nextHistory.length)
      }
    );
    setHistoryLoading(false);
  };

  const removeRating = (filmId: EntityId) => {
    if (!canMutateRatings() || filmId == null) return;

    const targetId = String(filmId);
    const currentRatings = isUnknownArray(ratingsStateRef.current)
      ? ratingsStateRef.current
      : [];
    const nextRatings = currentRatings.filter(
      (item) => String(item.filmId || item.id) !== targetId
    );

    if (nextRatings.length === currentRatings.length) return;

    const nextFilmMap = { ...filmMapRef.current };
    delete nextFilmMap[targetId];

    persistRatings(nextRatings, nextFilmMap, undefined, {
      lastSyncedAt: Date.now()
    });
  };

  useEffect(() => {
    const handleFilmCreditsUpdate = (event: Event) => {
      if (!(event instanceof CustomEvent) || !isRecord(event.detail)) return;
      const credits: FilmMetadata = event.detail;
      if (!credits?.filmId) return;
      const currentRatings = isUnknownArray(ratingsStateRef.current)
        ? ratingsStateRef.current
        : [];
      const currentRating = currentRatings.find(
        (rating) => String(rating.filmId) === String(credits.filmId)
      );
      if (!currentRating) {
        return;
      }
      const incomingMediaType = getStoredMediaType(credits);
      const ratingMediaType = getStoredMediaType(currentRating);
      if (
        incomingMediaType &&
        ratingMediaType &&
        incomingMediaType !== ratingMediaType
      ) return;

      const nextCredits = {
        cast: credits.cast || [],
        crew: credits.crew || []
      };
      if (
        Number(currentRating.weeklyQuestMetadataVersion || 0) >=
          WEEKLY_QUEST_PERSON_METADATA_VERSION &&
        getCreditsSignature(currentRating) === getCreditsSignature(nextCredits)
      ) {
        return;
      }

      upsertRating({
        filmId: String(credits.filmId),
        ...nextCredits,
        directors: extractDirectors(nextCredits.crew),
        weeklyQuestMetadataVersion: WEEKLY_QUEST_PERSON_METADATA_VERSION,
        weeklyQuestMetadataUpdatedAt: new Date()
      });
    };

    window.addEventListener("film-credits-updated", handleFilmCreditsUpdate);
    return () => window.removeEventListener("film-credits-updated", handleFilmCreditsUpdate);
  // Состояние оценок читается из ref, чтобы событие из быстрого кэша не
  // потерялось из-за устаревшего массива ratings в замыкании.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId]);

  useEffect(() => {
    hydrationCompleteRef.current = false;

    if (!enabled || !userId) {
      setRatingsSyncMetadata({
        userId: userId || null,
        loaded: true,
        exists: false,
        revision: 0,
        ratingsCount: null,
        updatedAt: 0
      });
      return undefined;
    }

    // Demo ratings are a browser-local sandbox layered over the prepared
    // clone. A copied real-account revision is irrelevant and used to keep
    // ratingsSyncReady false forever (or until an unrelated listener fired).
    if (isDemoSession) {
      setRatingsSyncMetadata({
        userId,
        loaded: true,
        exists: false,
        revision: 0,
        ratingsCount: null,
        updatedAt: 0
      });
      return undefined;
    }

    setRatingsSyncMetadata({
      userId,
      loaded: false,
      exists: false,
      revision: 0,
      ratingsCount: null,
      updatedAt: 0
    });

    const userRef = doc(getFirestore(), "users", userId);
    return onSnapshot(
      userRef,
      (snapshot) => {
        const metadata = normalizeRatingsSyncMetadata(
          snapshot.exists() ? snapshot.data() : {}
        );
        setRatingsSyncMetadata((current) => {
          if (
            current.userId === userId &&
            current.loaded &&
            current.exists === metadata.exists &&
            current.revision === metadata.revision &&
            current.ratingsCount === metadata.ratingsCount
          ) {
            return current;
          }
          return { userId, loaded: true, ...metadata };
        });
      },
      (syncError) => {
        console.warn("Не удалось подписаться на ревизию оценок:", syncError);
        setRatingsSyncMetadata({
          userId,
          loaded: true,
          exists: false,
          revision: 0,
          ratingsCount: null,
          updatedAt: 0
        });
      }
    );
  }, [enabled, isDemoSession, userId]);

  useEffect(() => {
    if (
      enabled &&
      userId &&
      (
        !ratingsSyncMetadata.loaded ||
        ratingsSyncMetadata.userId !== userId
      )
    ) {
      return undefined;
    }
    if (
      hydrationCompleteRef.current &&
      ratingsSyncMetadata.exists &&
      serverRevisionRef.current === ratingsSyncMetadata.revision
    ) {
      return undefined;
    }

    if (!enabled || !userId) {
      ratingsStateRef.current = [];
      ratingHistoryStateRef.current = [];
      setRatings([]);
      setRatingHistory([]);
      filmMapRef.current = {};
      serverTimestampRef.current = 0;
      lastFullFetchAtRef.current = 0;
      lastSyncedAtRef.current = 0;
      serverCountRef.current = 0;
      lastCountCheckedAtRef.current = 0;
      historyTimestampRef.current = 0;
      historyLastFullFetchAtRef.current = 0;
      historyLastSyncedAtRef.current = 0;
      historyServerCountRef.current = 0;
      serverRevisionRef.current = 0;
      hydrationCompleteRef.current = true;
      setError(null);
      setLoading(false);
      setHistoryLoading(false);
      setInitialSyncLoading(false);
      return;
    }

    const db = getFirestore();
    const ratingsRef = collection(db, "userRatings", userId, "ratings");
    const historyRef = collection(db, "userRatings", userId, "history");

    let isActive = true;
    hydrationCompleteRef.current = false;
    setInitialSyncLoading(true);

    const loadMissingFilms = async (ratingEntries: Rating[] = [], currentFilmMap: Record<string, FilmMetadata> = {}) => {
      const ratingMap = new Map(
        ratingEntries.map((rating) => [String(rating.filmId), rating])
      );
      const nextFilmMap = { ...currentFilmMap };

      ratingMap.forEach((ratingMetadata, filmId) => {
        const filmMetadata = currentFilmMap[filmId] || {};
        if (hasUsefulFilmMetadata(ratingMetadata, filmMetadata)) {
          nextFilmMap[filmId] = {
            ...ratingMetadata,
            ...filmMetadata
          };
        }
      });

      const missingIds = ratingEntries
        .map((rating) => String(rating.filmId))
        .filter((filmId) => !hasUsefulFilmMetadata(
          ratingMap.get(filmId) || {},
          currentFilmMap[filmId] || {}
        ));

      if (missingIds.length === 0) {
        return nextFilmMap;
      }

      const filmChunks = chunk([...new Set(missingIds)], FILM_CHUNK_SIZE);
      const filmsRef = collection(db, "films");

      let snapshots: QuerySnapshot[];
      try {
        snapshots = await mapWithConcurrency(
          filmChunks,
          FILM_QUERY_CONCURRENCY,
          (ids) =>
            getDocs(query(filmsRef, where(documentId(), "in", ids)))
        );
      } catch (err) {
        // Метаданные фильма улучшают карточки, но их сбой не должен скрывать
        // уже загруженные оценки и историю пользователя.
        console.warn("Не удалось дозагрузить метаданные фильмов:", err);
        return nextFilmMap;
      }

      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((filmDoc) => {
          nextFilmMap[filmDoc.id] = filmDoc.data();
        });
      });

      return nextFilmMap;
    };

    const enrichMetadataInBackground = (
      entries: Rating[] = [],
      { updateRatings = true, updateHistory = true }: { updateRatings?: boolean; updateHistory?: boolean } = {}
    ) => {
      if (!isUnknownArray(entries) || entries.length === 0) return;

      // Ratings/history are the authoritative content and are enough to paint
      // statistics immediately. Film documents only enrich cards with posters,
      // titles and taxonomy, so they must never sit in the critical path for a
      // cold profile load (large libraries can require hundreds of chunked
      // reads here).
      void loadMissingFilms(entries, filmMapRef.current)
        .then((loadedFilmMap) => {
          if (!isActive) return;

          const nextFilmMap = {
            ...filmMapRef.current,
            ...loadedFilmMap
          };
          let persisted = false;

          if (updateRatings && ratingsStateRef.current.length) {
            persistRatings(
              mergeRatingsWithFilms(ratingsStateRef.current, nextFilmMap),
              nextFilmMap
            );
            persisted = true;
          }

          if (updateHistory && ratingHistoryStateRef.current.length) {
            persistRatingHistory(
              mergeRatingsWithFilms(ratingHistoryStateRef.current, nextFilmMap),
              nextFilmMap
            );
            persisted = true;
          }

          if (!persisted) {
            filmMapRef.current = nextFilmMap;
            void saveCacheSnapshot({ filmMap: nextFilmMap }).catch((err: unknown) => {
              console.warn("Failed to save film metadata to IndexedDB:", err);
            });
          }
        })
        .catch((err: unknown) => {
          // loadMissingFilms already treats metadata as optional, but keep the
          // detached task safe if a future implementation starts throwing.
          console.warn("Не удалось обновить метаданные фильмов в фоне:", err);
        });
    };

    const loadRatingHistoryFull = async () => {
      const stateAtRequestStart = ratingHistoryStateRef.current;
      setHistoryLoading(true);
      try {
        // Active history duplicates the canonical rating document almost
        // field-for-field. Download only deletion tombstones and reconstruct
        // active history locally; this removes thousands of duplicate reads on
        // a first login while preserving revocation semantics.
        const historySnapshot = await getDocs(
          query(historyRef, where("active", "==", false))
        );
        const historyEntries = mergeRatingsWithDeletedHistory(
          ratingsStateRef.current,
          historySnapshot.docs.map(toHistoryEntry)
        );
        if (!isActive) return false;
        if (ratingHistoryStateRef.current !== stateAtRequestStart) {
          setHistoryLoading(false);
          return true;
        }

        const reconciledRatings = reconcileRatingsWithHistoryTombstones(
          ratingsStateRef.current,
          historySnapshot.docs.map(toHistoryEntry)
        );
        if (reconciledRatings !== ratingsStateRef.current) {
          ratingsStateRef.current = reconciledRatings;
          setRatings(reconciledRatings);
        }

        const now = Date.now();
        persistRatingHistory(
          mergeRatingsWithFilms(historyEntries, filmMapRef.current),
          filmMapRef.current,
          getLatestServerTimestamp(historyEntries) || now,
          {
            historyLastFullFetchAt: now,
            historyLastSyncedAt: now,
            historyServerCount: historyEntries.length
          }
        );
        enrichMetadataInBackground(historyEntries);
        return true;
      } catch (err) {
        console.error("Ошибка загрузки истории оценок пользователя:", err);
        if (!isActive) return false;
        setHistoryLoading(false);
        return false;
      }
    };

    const touchHistoryCacheSync = (cachedData: Partial<RatingsCachePayload> | null) => {
      const now = Date.now();
      historyLastSyncedAtRef.current = now;
      void saveCacheSnapshot({
        ratingHistory: ratingHistoryStateRef.current,
        filmMap: filmMapRef.current,
        historyLastSyncedAt: now,
        historyServerCount:
          Number(cachedData?.historyServerCount) ||
          historyServerCountRef.current ||
          ratingHistoryStateRef.current.length
      }).catch((err: unknown) => {
        console.warn("Failed to update rating history cache sync marker:", err);
      });
    };

    const hydrateRatingHistory = async (
      cachedData: Partial<RatingsCachePayload>,
      { force = false, skipServerSync = false }: { force?: boolean; skipServerSync?: boolean } = {}
    ) => {
      const cachedHistory = isUnknownArray(cachedData?.ratingHistory)
        ? cachedData.ratingHistory
        : null;
      if (cachedHistory) {
        ratingHistoryStateRef.current = cachedHistory;
        setRatingHistory(cachedHistory);
        setHistoryLoading(false);
        historyTimestampRef.current = Number(cachedData.historyLastServerTimestamp) || 0;
        historyLastFullFetchAtRef.current = Number(cachedData.historyLastFullFetchAt) || 0;
        historyLastSyncedAtRef.current = Number(cachedData.historyLastSyncedAt) || 0;
        historyServerCountRef.current = Number(cachedData.historyServerCount) || cachedHistory.length;
      } else {
        setHistoryLoading(true);
      }

      if (skipServerSync) return true;

      if (
        !force &&
        cachedHistory &&
        Date.now() - Number(cachedData?.historyLastSyncedAt || 0) < HISTORY_DELTA_SYNC_TTL
      ) {
        return true;
      }

      const watermark = Number(cachedData?.historyLastServerTimestamp) || 0;
      if (!cachedHistory || !watermark) {
        const lastFullFetchAt = Number(cachedData?.historyLastFullFetchAt) || 0;
        if (
          !force &&
          cachedHistory &&
          Date.now() - lastFullFetchAt < HISTORY_FULL_REFRESH_TTL
        ) {
          setHistoryLoading(false);
          return true;
        }
        return loadRatingHistoryFull();
      }

      try {
        const historyDeltaQuery = query(
          historyRef,
          where("updatedAt", ">=", Timestamp.fromMillis(watermark))
        );
        const historySnapshot = await getDocs(historyDeltaQuery);
        if (!isActive) return false;

        if (historySnapshot.empty) {
          setHistoryLoading(false);
          touchHistoryCacheSync(cachedData);
          return true;
        }

        const changedEntries = historySnapshot.docs.map(toHistoryEntry);
        const mergedFilmMap = filmMapRef.current;

        const changedIds = new Set(changedEntries.map((entry) => entry.filmId));
        const normalizedChangedMap = new Map(
          changedEntries.map((entry) => [
            entry.filmId,
            normalizeFilmMetadata(
              entry.filmId,
              entry,
              mergedFilmMap[String(entry.filmId)] || {}
            )
          ])
        );
        const baseHistory = isUnknownArray(ratingHistoryStateRef.current)
          ? ratingHistoryStateRef.current
          : [];
        const updatedExistingHistory = baseHistory.map((entry) =>
          changedIds.has(entry.filmId)
            ? preferNewestEntry(entry, normalizedChangedMap.get(entry.filmId))
            : entry
        );
        const existingIds = new Set(baseHistory.map((entry) => entry.filmId));
        const newHistory = changedEntries
          .filter((entry) => !existingIds.has(entry.filmId))
          .map((entry) => normalizedChangedMap.get(entry.filmId))
          .filter((entry): entry is Rating => Boolean(entry));
        const nextHistory = [...newHistory, ...updatedExistingHistory];
        const reconciledRatings = reconcileRatingsWithHistoryTombstones(
          ratingsStateRef.current,
          changedEntries
        );
        if (reconciledRatings !== ratingsStateRef.current) {
          ratingsStateRef.current = reconciledRatings;
          setRatings(reconciledRatings);
        }
        const now = Date.now();
        persistRatingHistory(
          nextHistory,
          mergedFilmMap,
          Math.max(watermark, getLatestServerTimestamp(changedEntries)),
          {
            historyLastFullFetchAt: Number(cachedData.historyLastFullFetchAt) || 0,
            historyLastSyncedAt: now,
            historyServerCount: Math.max(
              Number(cachedData.historyServerCount) || 0,
              nextHistory.length
            )
          }
        );
        enrichMetadataInBackground(changedEntries);
        return true;
      } catch (err) {
        console.error("Ошибка дозагрузки истории оценок пользователя:", err);
        if (!cachedHistory) return loadRatingHistoryFull();
        if (isActive) setHistoryLoading(false);
        return false;
      }
    };

    const loadRatings = async () => {
      const stateAtRequestStart = ratingsStateRef.current;
      try {
        const ratingsSnapshot = await getDocs(ratingsRef);
        const ratingEntries = ratingsSnapshot.docs.map(toRatingEntry);

        if (!isActive) return false;
        // A local save/delete can finish while this full request is in
        // flight. Never replace that newer state with an older query result;
        // the next delta sync will reconcile the server watermark cheaply.
        if (ratingsStateRef.current !== stateAtRequestStart) {
          setLoading(false);
          return true;
        }

        const mergedFilmMap = filmMapRef.current;
        const mergedRatings = mergeRatingsWithFilms(ratingEntries, mergedFilmMap);
        const now = Date.now();
        persistRatings(
          mergedRatings,
          mergedFilmMap,
          getLatestServerTimestamp(ratingEntries) || now,
          {
            lastFullFetchAt: now,
            lastSyncedAt: now,
            serverCount: ratingsSnapshot.size,
            lastCountCheckedAt: now
          }
        );
        enrichMetadataInBackground(ratingEntries, { updateHistory: false });
        return true;
      } catch (err) {
        console.error("Ошибка загрузки рейтингов пользователя:", err);
        if (!isActive) return false;
        setError(err instanceof Error ? err : new Error(errorMessage(err)));
        setLoading(false);
        return false;
      }
    };

    const loadRatingsAndHistoryFull = async () => {
      setLoading(true);
      setHistoryLoading(true);

      // Start both reads together, but publish the active ratings as soon as
      // their query finishes. Deleted history is needed for reconciliation and
      // mutations, not for the first statistics paint; waiting for the larger
      // history query used to keep a cold-cache page blank for several extra
      // seconds on 3G.
      const ratingsRequest = getDocs(ratingsRef).then(
        (value): SnapshotLoadResult => ({ status: "fulfilled", value }),
        (reason: unknown): SnapshotLoadResult => ({ status: "rejected", reason })
      );
      const historyRequest = getDocs(
        query(historyRef, where("active", "==", false))
      ).then(
        (value): SnapshotLoadResult => ({ status: "fulfilled", value }),
        (reason: unknown): SnapshotLoadResult => ({ status: "rejected", reason })
      );

      const ratingsResult = await ratingsRequest;
      if (!isActive) return false;

      const ratingsSnapshot =
        ratingsResult.status === "fulfilled" ? ratingsResult.value : null;
      const ratingsError = ratingsResult.status === "rejected" ? ratingsResult.reason : null;
      const ratingEntries = ratingsSnapshot
        ? ratingsSnapshot.docs.map(toRatingEntry)
        : [];
      const mergedFilmMap = filmMapRef.current;
      const now = Date.now();
      if (ratingsSnapshot) {
        persistRatings(
          mergeRatingsWithFilms(ratingEntries, mergedFilmMap),
          mergedFilmMap,
          getLatestServerTimestamp(ratingEntries) || now,
          {
            lastFullFetchAt: now,
            lastSyncedAt: now,
            serverCount: ratingsSnapshot.size,
            lastCountCheckedAt: now
          }
        );
      } else {
        console.error(
          "Ошибка загрузки рейтингов пользователя:",
          ratingsError
        );
        setError(ratingsError instanceof Error ? ratingsError : new Error(errorMessage(ratingsError)));
        setLoading(false);
      }

      // Metadata enrichment is optional and can begin without waiting for
      // deleted history. This also lets poster/title requests overlap the
      // remaining background reconciliation.
      enrichMetadataInBackground(ratingEntries, { updateHistory: false });

      const historyResult = await historyRequest;
      if (!isActive) return false;

      const historySnapshot =
        historyResult.status === "fulfilled" ? historyResult.value : null;
      const historyError = historyResult.status === "rejected" ? historyResult.reason : null;
      const deletedHistoryEntries = historySnapshot
        ? historySnapshot.docs.map(toHistoryEntry)
        : [];
      const historyEntries = mergeRatingsWithDeletedHistory(
        ratingEntries,
        deletedHistoryEntries
      );

      if (historySnapshot) {
        persistRatingHistory(
          mergeRatingsWithFilms(historyEntries, mergedFilmMap),
          mergedFilmMap,
          getLatestServerTimestamp(historyEntries) || now,
          {
            historyLastFullFetchAt: now,
            historyLastSyncedAt: now,
            historyServerCount: historyEntries.length
          }
        );
      } else if (ratingsSnapshot) {
        // Ratings are still authoritative for active lifecycles. Keep the
        // page usable if the optional tombstone query is offline/forbidden.
        persistRatingHistory(
          mergeRatingsWithFilms(historyEntries, mergedFilmMap),
          mergedFilmMap,
          getLatestServerTimestamp(historyEntries) || now,
          {
            historyLastFullFetchAt: 0,
            historyLastSyncedAt: now,
            historyServerCount: historyEntries.length
          }
        );
        console.warn(
          "Не удалось загрузить удалённые записи истории; активная история восстановлена из оценок:",
          historyError
        );
      } else {
        console.error(
          "Ошибка загрузки истории оценок пользователя:",
          historyError
        );
        setHistoryLoading(false);
      }

      enrichMetadataInBackground(deletedHistoryEntries, { updateRatings: false });
      return Boolean(ratingsSnapshot && historySnapshot);
    };

    const ensureCachedCountIsCurrent = async (
      cachedData: Partial<RatingsCachePayload>,
      { force = false }: { force?: boolean } = {}
    ) => {
      const cachedRatings = isUnknownArray(ratingsStateRef.current)
        ? ratingsStateRef.current
        : [];
      const stateAtRequestStart = ratingsStateRef.current;
      const cachedCount = cachedRatings.length;
      const countAge = Date.now() - Number(cachedData?.lastCountCheckedAt || 0);
      const cachedServerCount = Number(cachedData?.serverCount);

      if (
        !force &&
        countAge < COUNT_SYNC_TTL &&
        Number.isFinite(cachedServerCount) &&
        cachedServerCount === cachedCount
      ) {
        return true;
      }

      try {
        const countSnapshot = await getCountFromServer(ratingsRef);
        const serverCount = Number(countSnapshot.data().count || 0);
        if (!isActive) return null;
        if (ratingsStateRef.current !== stateAtRequestStart) {
          // A mutation has already produced a newer IndexedDB snapshot. Do
          // not overwrite it with the array captured before the count query.
          return true;
        }

        if (serverCount !== cachedCount) {
          serverCountRef.current = serverCount;
          return false;
        }

        const now = Date.now();
        serverCountRef.current = serverCount;
        lastCountCheckedAtRef.current = now;
        void saveCacheSnapshot({
          ratings: cachedRatings,
          filmMap: filmMapRef.current,
          serverCount,
          lastCountCheckedAt: now,
          posterRepairVersion: cachedData.posterRepairVersion || POSTER_REPAIR_VERSION
        }).catch((err: unknown) => {
          console.warn("Failed to update ratings count cache marker:", err);
        });
        return true;
      } catch (err) {
        console.warn("Не удалось проверить количество оценок на сервере:", err);
        return null;
      }
    };

    const loadRatingsDelta = async (cachedData: Partial<RatingsCachePayload>, { force = false }: { force?: boolean } = {}) => {
      const stateAtRequestStart = ratingsStateRef.current;
      try {
        if (!isUnknownArray(cachedData?.ratings)) {
          return loadRatings();
        }

        const watermark = Number(cachedData.lastServerTimestamp) || 0;
        if (!watermark) {
          const lastFullFetchAt = Number(cachedData.lastFullFetchAt) || 0;
          if (!force && Date.now() - lastFullFetchAt < FULL_REFRESH_TTL) {
            setLoading(false);
            return true;
          }
          return loadRatings();
        }
        // Для пустого кэша запрос коллекции корректен: пустой результат тоже
        // является валидными данными и больше не запускает второй full fetch.
        const deltaQuery = query(
          ratingsRef,
          where("updatedAt", ">=", Timestamp.fromMillis(watermark))
        );
        const ratingsSnapshot = await getDocs(deltaQuery);

        if (!isActive) return false;

        if (ratingsSnapshot.empty) {
          setLoading(false);
          if (ratingsStateRef.current !== stateAtRequestStart) return true;
          lastSyncedAtRef.current = Date.now();
          void saveCacheSnapshot({
            lastSyncedAt: lastSyncedAtRef.current
          }).catch((err: unknown) => {
            console.warn("Failed to update ratings cache sync marker:", err);
          });
          return true;
        }

        const changedEntries = ratingsSnapshot.docs.map(toRatingEntry);
        const mergedFilmMap = filmMapRef.current;

        const changedIds = new Set(changedEntries.map((entry) => entry.filmId));
        const normalizedChangedMap = new Map(
          changedEntries.map((entry) => [
            entry.filmId,
            normalizeFilmMetadata(
              entry.filmId,
              entry,
              mergedFilmMap[String(entry.filmId)] || {}
            )
          ])
        );

        // Preserve a rating changed locally while the delta query was in
        // flight. Remote entries are merged into the latest state, not into
        // the stale payload captured when hydration began.
        const baseRatings = ratingsStateRef.current || [];
        const existingIds = new Set(baseRatings.map((entry) => entry.filmId));
        const updatedExistingRatings = baseRatings.map((entry) =>
          changedIds.has(entry.filmId)
            ? preferNewestEntry(entry, normalizedChangedMap.get(entry.filmId))
            : entry
        );
        const newRatings = changedEntries
          .filter((entry) => !existingIds.has(entry.filmId))
          .map((entry) => normalizedChangedMap.get(entry.filmId))
          .filter((entry): entry is Rating => Boolean(entry));

        const nextRatings = [...newRatings, ...updatedExistingRatings];
        const now = Date.now();
        persistRatings(nextRatings, mergedFilmMap, Math.max(
          watermark,
          getLatestServerTimestamp(changedEntries)
        ), {
          lastFullFetchAt: lastFullFetchAtRef.current,
          lastSyncedAt: now
        });
        enrichMetadataInBackground(changedEntries, { updateHistory: false });
        return true;
      } catch (err) {
        console.error("Ошибка дозагрузки рейтингов пользователя:", err);
        // При наличии кэша сетевой сбой не должен превращать страницу в
        // «нет данных» и не должен провоцировать дорогое полное чтение.
        if (!cachedData) return loadRatings();
        if (isActive) setLoading(false);
        return false;
      }
    };

    const persistServerRevision = async (metadata: Pick<RatingsSyncState, "exists" | "ratingsCount" | "revision"> | null) => {
      if (!metadata?.exists) return;
      const serverCount = toOptionalCount(metadata.ratingsCount);
      const now = Date.now();
      serverRevisionRef.current = Number(metadata.revision) || 0;
      if (serverCount !== null) {
        serverCountRef.current = serverCount;
        lastCountCheckedAtRef.current = now;
      }
      await saveCacheSnapshot({
        serverRevision: serverRevisionRef.current,
        serverCount: serverCount !== null
          ? serverCount
          : ratingsStateRef.current.length,
        lastCountCheckedAt: serverCount !== null
          ? now
          : lastCountCheckedAtRef.current,
        posterRepairVersion: POSTER_REPAIR_VERSION
      });
    };

    const bootstrapRatingsSync = async () => {
      if (getAuth().currentUser?.uid !== userId) return null;
      const metadata = {
        exists: true,
        version: RATINGS_SYNC_VERSION,
        revision: 1,
        ratingsCount: ratingsStateRef.current.length,
        updatedAt: Date.now()
      };
      const previousRevision = serverRevisionRef.current;

      try {
        serverRevisionRef.current = metadata.revision;
        await setDoc(
          doc(db, "users", userId),
          {
            ratingsSync: {
              version: RATINGS_SYNC_VERSION,
              revision: metadata.revision,
              ratingsCount: metadata.ratingsCount,
              updatedAt: serverTimestamp()
            }
          },
          { merge: true }
        );
        await persistServerRevision(metadata);
        return metadata;
      } catch (syncError) {
        serverRevisionRef.current = previousRevision;
        console.warn("Не удалось создать ревизию кэша оценок:", syncError);
        return null;
      }
    };

    const syncChangedRatings = async (cachedData: Partial<RatingsCachePayload>, metadata: Pick<RatingsSyncState, "exists" | "ratingsCount" | "revision"> | null) => {
      const historySynced = await hydrateRatingHistory(cachedData, { force: true });
      if (!isActive || !historySynced) return false;

      const ratingsSynced = await loadRatingsDelta(cachedData, { force: true });
      if (!isActive || !ratingsSynced) return false;

      const metadataCount = toOptionalCount(metadata?.ratingsCount);
      let countMatches = metadataCount !== null
        ? ratingsStateRef.current.length === metadataCount
        : await ensureCachedCountIsCurrent(cachedData, { force: true });
      if (countMatches === null) return false;

      if (!countMatches) {
        // A delta cannot observe a legacy hard delete without a history
        // tombstone. This is the only changed-data path that needs a full scan.
        const fullSyncSucceeded = await loadRatingsAndHistoryFull();
        if (!isActive || !fullSyncSucceeded) return false;
        countMatches = metadataCount === null ||
          ratingsStateRef.current.length === metadataCount;
      }

      if (metadata?.exists && countMatches) {
        await persistServerRevision(metadata);
      }
      return countMatches;
    };

    const hydrate = async () => {
      try {
        const cachedData = await loadUserRatingsFromCache(userId);
        if (!isActive) return;

        if (cachedData) {
          ratingsStateRef.current = cachedData.ratings || [];
          setRatings(cachedData.ratings || []);
          filmMapRef.current = cachedData.filmMap || {};
          serverTimestampRef.current =
            Number(cachedData.lastServerTimestamp) || 0;
          lastFullFetchAtRef.current = Number(cachedData.lastFullFetchAt) || 0;
          lastSyncedAtRef.current = Number(cachedData.lastSyncedAt) || 0;
          serverCountRef.current = Number(cachedData.serverCount) || 0;
          lastCountCheckedAtRef.current = Number(cachedData.lastCountCheckedAt) || 0;
          serverRevisionRef.current = Number(cachedData.serverRevision) || 0;
          setLoading(false);

          const hydratedData = {
            ...cachedData,
            posterRepairVersion: POSTER_REPAIR_VERSION
          };
          // Demo changes intentionally live only in this browser. The server
          // clone remains read-only, so its unchanged revision/count must not
          // overwrite locally added or removed ratings on the next mount.
          if (isDemoSession) {
            await hydrateRatingHistory(hydratedData, {
              skipServerSync: true
            });
            return;
          }
          const remoteRevisionMatches =
            ratingsSyncMetadata.exists &&
            Number(cachedData.serverRevision) === ratingsSyncMetadata.revision &&
            (
              !ratingsSyncMetadata.updatedAt ||
              Number(cachedData.lastServerTimestamp || 0) >= ratingsSyncMetadata.updatedAt
            ) &&
            (
              toOptionalCount(ratingsSyncMetadata.ratingsCount) === null ||
              toOptionalCount(ratingsSyncMetadata.ratingsCount) ===
                (cachedData.ratings || []).length
            );

          if (remoteRevisionMatches) {
            // The single user-document revision proves that neither ratings nor
            // history changed. Hydrate only IndexedDB and issue no collection
            // queries.
            await hydrateRatingHistory(hydratedData, {
              skipServerSync: true
            });
            return;
          }

          if (ratingsSyncMetadata.exists) {
            await syncChangedRatings(hydratedData, ratingsSyncMetadata);
            return;
          }

          // One-time migration for existing accounts: reconcile deltas first,
          // use a count aggregation only as a guard, then create the durable
          // revision marker. Future mounts read no rating collections unless
          // that marker changes.
          const migrationSynced = await syncChangedRatings(hydratedData, null);
          if (!isActive) return;
          if (migrationSynced) await bootstrapRatingsSync();
        } else {
          ratingsStateRef.current = [];
          ratingHistoryStateRef.current = [];
          setRatings([]);
          setRatingHistory([]);
          filmMapRef.current = {};
          serverTimestampRef.current = 0;
          lastFullFetchAtRef.current = 0;
          lastSyncedAtRef.current = 0;
          serverCountRef.current = 0;
          lastCountCheckedAtRef.current = 0;
          historyTimestampRef.current = 0;
          historyLastFullFetchAtRef.current = 0;
          historyLastSyncedAtRef.current = 0;
          historyServerCountRef.current = 0;
          serverRevisionRef.current = 0;
          setError(null);
          setLoading(true);
          setHistoryLoading(true);
          const fullSyncSucceeded = await loadRatingsAndHistoryFull();
          if (!isActive) return;
          if (!fullSyncSucceeded) return;
          if (isDemoSession) {
            return;
          } else if (ratingsSyncMetadata.exists) {
            await persistServerRevision(ratingsSyncMetadata);
          } else {
            await bootstrapRatingsSync();
          }
        }
      } finally {
        if (isActive) {
          hydrationCompleteRef.current = true;
          setInitialSyncLoading(false);
        }
      }
    };

    hydrate();

    return () => {
      isActive = false;
    };
  // Helper functions intentionally stay inside the lifecycle that owns their
  // cancellation flag and Firestore references.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    ratingsSyncMetadata.exists,
    ratingsSyncMetadata.loaded,
    ratingsSyncMetadata.ratingsCount,
    ratingsSyncMetadata.revision,
    ratingsSyncMetadata.userId,
    isDemoSession,
    userId
  ]);

  return (
    <UserRatingsContext.Provider value={{
      userId: ratingsOwnerUserId,
      ratings: enabled ? ratings : [],
      ratingHistory: enabled ? ratingHistory : [],
      loading: enabled ? loading : false,
      historyLoading: enabled ? historyLoading : false,
      initialSyncLoading: enabled ? initialSyncLoading : false,
      ratingsSyncRevision:
        enabled && ratingsSyncMetadata.userId === ratingsOwnerUserId
          ? Number(ratingsSyncMetadata.revision) || 0
          : 0,
      ratingsSyncReady: Boolean(
        enabled &&
        ratingsSyncMetadata.loaded &&
        ratingsSyncMetadata.userId === ratingsOwnerUserId &&
        !initialSyncLoading &&
        hydrationCompleteRef.current &&
        (
          isDemoSession ||
          !ratingsSyncMetadata.exists ||
          serverRevisionRef.current === ratingsSyncMetadata.revision
        )
      ),
      error: enabled ? error : null,
      upsertRating,
      upsertRatings,
      upsertRatingHistory,
      removeRating
    }}>
      {children}
    </UserRatingsContext.Provider>
  );
}
